/**
 * Which script a piece of text is written in — RX-I18N-05, RX-MED-01.
 *
 * Needed because a medicine name's "original" language is not a constant. An
 * Indian strip usually prints GLYCOMET in Latin; a box in Thailand, Greece or
 * much of the Arab world prints the name in the local script; and an Indian
 * box may print both. There is no side of that which is universally the real
 * one.
 *
 * So DrBell does not decide. It records the name exactly as the person typed
 * it from the packet, notes which script that turned out to be, and lets them
 * add a second label in another script if they want one. Both are typed by a
 * human. Neither is ever generated, transliterated or translated — doing that
 * would produce a word that is not printed on anything, which is the one thing
 * a medication reminder must never show.
 *
 * Detection is here only so the app can tell whether the two labels are
 * genuinely in different scripts, and so it can lay them out correctly. It
 * never changes what is stored.
 */

export type ScriptCode =
  | 'Latn' | 'Deva' | 'Telu' | 'Taml' | 'Beng' | 'Knda' | 'Mlym' | 'Gujr'
  | 'Guru' | 'Orya' | 'Arab' | 'Thai' | 'Hani' | 'Cyrl' | 'Grek' | 'Hebr'
  /** Digits, punctuation and spaces only — "500", "-", "/". */
  | 'Zyyy'
  /** Nothing recognised, or empty. */
  | 'Zzzz';

/**
 * Ranges, most specific first.
 *
 * Deliberately a small table rather than a dependency: this needs to run in a
 * Worker, it is checked against a handful of scripts a medicine box is
 * plausibly printed in, and a full Unicode property table is megabytes.
 */
const RANGES: ReadonlyArray<[ScriptCode, RegExp]> = [
  ['Deva', /[ऀ-ॿ]/],
  ['Beng', /[ঀ-৿]/],
  ['Guru', /[਀-੿]/],
  ['Gujr', /[઀-૿]/],
  ['Orya', /[଀-୿]/],
  ['Taml', /[஀-௿]/],
  ['Telu', /[ఀ-౿]/],
  ['Knda', /[ಀ-೿]/],
  ['Mlym', /[ഀ-ൿ]/],
  ['Thai', /[฀-๿]/],
  ['Arab', /[؀-ۿݐ-ݿ]/],
  ['Hebr', /[֐-׿]/],
  ['Grek', /[Ͱ-Ͽ]/],
  ['Cyrl', /[Ѐ-ӿ]/],
  ['Hani', /[一-鿿㐀-䶿]/],
  ['Latn', /[A-Za-zÀ-ɏ]/],
];

/**
 * The dominant script of a string.
 *
 * "Dominant" rather than "only", because a real label is mixed: `GLYCOMET 500`
 * is Latin with digits, and `గ్లైకోమెట్ 500` is Telugu with digits. Digits and
 * punctuation are common to both and never decide the answer.
 */
export function detectScript(text: string): ScriptCode {
  const counts = new Map<ScriptCode, number>();

  for (const ch of text) {
    for (const [code, re] of RANGES) {
      if (re.test(ch)) {
        counts.set(code, (counts.get(code) ?? 0) + 1);
        break;
      }
    }
  }

  if (counts.size === 0) {
    return text.trim() === '' ? 'Zzzz' : /[0-9]/.test(text) ? 'Zyyy' : 'Zzzz';
  }

  let best: ScriptCode = 'Zzzz';
  let bestCount = 0;
  for (const [code, n] of counts) {
    if (n > bestCount) {
      best = code;
      bestCount = n;
    }
  }
  return best;
}

/** Scripts that read right to left, for laying a label out. */
const RTL = new Set<ScriptCode>(['Arab', 'Hebr']);

export function isRtlScript(script: ScriptCode): boolean {
  return RTL.has(script);
}

export interface MedicationLabel {
  /** As written on the packet, by the person holding it. Authoritative. */
  name: string;
  /**
   * A second label the same person chose to add, in another script.
   *
   * Their own words, not from the prescription and not derived from `name`.
   * Optional, and absent far more often than present.
   */
  nameAlt?: string | null;
}

export interface RenderedLabel {
  /** What to show, both labels when there are two. */
  text: string;
  primaryScript: ScriptCode;
  altScript: ScriptCode | null;
  /** True when the two labels are in genuinely different scripts. */
  bilingual: boolean;
}

/**
 * How to show a medicine name.
 *
 * The name from the packet always comes first and is always present. A second
 * label follows in brackets — never replaces, because the whole purpose of the
 * first one is matching what is physically in the drawer.
 *
 * A second label in the SAME script as the first is shown too, because it is
 * still something the person deliberately wrote; it just is not bilingual.
 */
export function renderMedicationLabel(label: MedicationLabel): RenderedLabel {
  const name = label.name.trim();
  const alt = (label.nameAlt ?? '').trim();

  const primaryScript = detectScript(name);
  if (alt === '' || alt === name) {
    return { text: name, primaryScript, altScript: null, bilingual: false };
  }

  const altScript = detectScript(alt);
  return {
    text: `${name} (${alt})`,
    primaryScript,
    altScript,
    bilingual: altScript !== primaryScript,
  };
}
